import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  Modal,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { makeStyles } from "@mui/styles";
import React from "react";
import { Theme } from "@mui/material/styles";

const useStyle = makeStyles<Theme, ModalProps>((theme) => ({
  sendClassCredentialsModal: {
    position: "absolute",
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
  modalTitle: {
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightBold,
    fontSize: "20px",
  },
  closeButton: {
    color: theme.palette.text.secondary,
  },
  buttonNo: {
    width: (props) => (props.ctaFooterVariant === "stretched" ? "100%" : "auto"),
    borderRadius: 16,
    backgroundColor: "#D8D8D8",
    color: theme.palette.text.primary,
    "&:hover": {
      backgroundColor: "#C9C8C8",
    },
  },
  buttonYes: {
    width: (props) => (props.ctaFooterVariant === "stretched" ? "100%" : "auto"),
    borderRadius: 16,
  },
}));

interface ModalProps {
  title: string;
  promptOpen: boolean;
  handleClose: (e: any) => void;
  handleOkClick: (e: any) => void;
  handleCancelClick: (e: any) => void;
  okButtonLabel: string;
  cancelButtonLabel?: string;
  children: React.ReactNode;
  ctaFooterVariant: "stretched" | "default";
}

export const SendClassCredentialsModal = (props: ModalProps) => {
  const classes = useStyle(props);
  const vertBreakpointSm = useMediaQuery("(max-height:755px)");
  const horiBreakpointSm = useMediaQuery("(max-width:769px)");
  const {
    handleOkClick,
    handleCancelClick,
    okButtonLabel,
    cancelButtonLabel,
    promptOpen,
    children,
    handleClose,
    title,
  } = props;

  return (
    <>
      <Modal
        open={promptOpen}
        onClose={props.handleClose}
        aria-labelledby="send-class-credentials-title">
        <Card
          sx={{ width: horiBreakpointSm ? "600px" : "900px" }}
          className={classes.sendClassCredentialsModal}>
          <CardHeader
            sx={{ padding: "16px 24px" }}
            title={
              <Typography
                id="edit-account-modal-title"
                variant="h6"
                className={classes.modalTitle}>
                {title}
              </Typography>
            }
            action={
              <Tooltip
                placement="bottom-end"
                title="Close"
                arrow
                role="tooltip"
                describeChild>
                <IconButton
                  onClick={(e) => {
                    handleClose(e);
                  }}
                  aria-label="close edit user account"
                  className={classes.closeButton}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            }
          />
          <Divider />
          <CardContent
            sx={{
              padding: "24px 32px",
              maxHeight: vertBreakpointSm ? "300px" : "400px",
              overflow: "auto",
            }}>
            {children}
          </CardContent>
          <Divider />
          <CardActions sx={{ padding: "24px 48px", justifyContent: "flex-end" }}>
            <Stack direction="row" justifyContent="flex-end" gap={"16px"}>
              {cancelButtonLabel && (
                <Button
                  disableRipple={true}
                  onClick={(e) => {
                    handleCancelClick(e);
                  }}
                  variant="contained"
                  color="primary"
                  size="large"
                  className={classes.buttonNo}>
                  {cancelButtonLabel}
                </Button>
              )}
              <Button
                disableRipple={true}
                onClick={(e) => {
                  handleOkClick(e);
                }}
                variant="contained"
                color="primary"
                size="large"
                className={classes.buttonYes}>
                {okButtonLabel}
              </Button>
            </Stack>
          </CardActions>
        </Card>
      </Modal>
    </>
  );
};
